import { Head, router } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import PageTitle from '@/components/PageTitle'
import { Button } from '@/components/ui/Button'
import { plan_product_pricings_edit_path, plan_product_pricings_index_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import PlanNavRow from '../components/PlanNavRow'
import PlanHeader from '../components/PlanHeader'
import ConnectedTaskLine from '../components/ConnectedTaskLine'
import { type BlockedReason } from './NoCurrentPlan'
import type { PricingPageProps } from './pricingTypes'
import PricingChannelTabs from './components/PricingChannelTabs'
import PricingNotice from './components/PricingNotice'
import PricingTable from './components/PricingTable'

// Show page: every member can read the channel's products and prices here.
// Editing happens on ProductPricingsEdit, reached through the
// "Work on Product & Pricing" button (holders of
// planning.manage_products_and_pricing on the selected channel, its market,
// or root — `can` is computed per channel server-side).
interface Props extends Partial<Omit<PricingPageProps, 'plan' | 'plan_switcher'>> {
  plan: PricingPageProps['plan'] | null
  plan_switcher: PricingPageProps['plan_switcher']
  blocked?: BlockedReason | null
}

const BLOCKED_MESSAGES: Record<BlockedReason, string> = {
  no_plan: 'No published top-down plan selected. Select a published plan to view this page.',
  draft: 'The current specified Top Down plan is not published yet. It needs to be published before viewing this page.',
  permission: 'You need the "view product pricings" permission for this plan. Ask an admin to grant it to your role.',
}

export default function ProductPricings({
  plan,
  price_levels = [],
  channels = [],
  channel = null,
  groups = [],
  cycle_task = null,
  blocked = null,
  plan_switcher,
  can = { edit_product_pricings: false },
}: Props) {
  const market = useMarket()
  const redirectPath = plan_product_pricings_index_path(market.slug)

  const header = plan ? (
    <PlanHeader
      name={plan.name}
      orgNodeName={plan.org_node?.name ?? null}
      revenueTarget={plan.revenue_target}
      currency={plan.currency}
      beginDate={plan.begin_date}
      endDate={plan.end_date}
    />
  ) : (
    <PageTitle title="Products & Pricings" />
  )

  if (!plan || blocked) {
    return (
      <AppLayout header={header}>
        <PlanNavRow planSwitcher={plan_switcher} redirectPath={redirectPath} className="mb-4" />
        <PricingNotice>{BLOCKED_MESSAGES[blocked ?? 'no_plan']}</PricingNotice>
      </AppLayout>
    )
  }

  return (
    <AppLayout header={header}>
      <Head title="Products & Pricings" />
      <PlanNavRow planSwitcher={plan_switcher} redirectPath={redirectPath} className="mb-4" />
      <div className="mb-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div />
        <PricingNotice>
          {channel ? (
            <>
              You are viewing products and prices for <span className="font-medium">{channel.name}</span>.
            </>
          ) : (
            'No sale channels under this plan yet.'
          )}
          {cycle_task && <ConnectedTaskLine task={cycle_task} />}
        </PricingNotice>
        {can.edit_product_pricings && channel && (
          <Button
            size="sm"
            className="justify-self-end"
            onClick={() => router.visit(plan_product_pricings_edit_path(market.slug, plan.slug, { channel: channel.slug }))}
          >
            Work on Product & Pricing
          </Button>
        )}
      </div>
      {channel && (
        <div className="overflow-hidden rounded-md border bg-card">
          <PricingChannelTabs
            channels={channels}
            activeSlug={channel.slug}
            hrefFor={(slug) => plan_product_pricings_index_path(market.slug, { channel: slug })}
          />
          <PricingTable
            groups={groups}
            priceLevels={price_levels}
            canEdit={false}
            emptyMessage={`No products for ${channel.name} yet.`}
          />
        </div>
      )}
    </AppLayout>
  )
}
